import React, { useState, useRef, useEffect } from 'react'
import { Input, Button, Card, Typography, Empty, Descriptions } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import ImageZoom from 'react-medium-image-zoom'
import { useAuth0 } from '../../auth0-wrapper'
import { getProductDetailBySKU } from './getProductDetailBySKU'

const { Title } = Typography

const IMAGE_KEYS = [
  'custitem_velprod_itemimage_1',
  'custitem_velprod_itemimage_2',
  'custitem_velprod_itemimage_3',
  'custitem_velprod_itemimage_4',
  'custitem_velprod_itemimage_5',
  'custitem_velprod_itemimage_6',
  'custitem_velprod_itemimage_7',
  'custitem_velprod_itemimage_8',
  'custitem_velprod_itemimage_9',
  'custitem_velprod_itemimage_10',
  'custitem_velprod_itemimage_11',
  'custitem_velprod_itemimage_12',
]

const SearchProductInfo = () => {
  const [sku, setSku] = useState('')
  const [searchedSku, setSearchedSku] = useState('')
  const [loading, setLoading] = useState(false)
  const [product, setProduct] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [failedImages, setFailedImages] = useState(new Set())
  const { getTokenSilently } = useAuth0()
  const inputRef = useRef(null)

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSearch = async () => {
    if (!sku.trim() || loading) return

    setLoading(true)
    setHasSearched(false)
    setProduct(null)
    setFailedImages(new Set())
    setSearchedSku(sku)

    try {
      const token = await getTokenSilently()
      const result = await getProductDetailBySKU(sku, token)
      setProduct(result)
    } catch (error) {
      console.error('Error searching for product:', error)
      setProduct(null)
    } finally {
      setLoading(false)
      setHasSearched(true)
      inputRef.current?.select()
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const images = product
    ? IMAGE_KEYS.map(key => product[key]).filter(Boolean)
    : []

  return (
    <Container>
      <Card bodyStyle={{ padding: '16px 24px' }}>
        <SearchSection>
          <SearchInput
            ref={inputRef}
            size="large"
            placeholder="Inserisci SKU (es. 0146380)"
            value={sku}
            onChange={e => setSku(e.target?.value?.toUpperCase())}
            onKeyDown={handleKeyDown}
            prefix={<SearchOutlined />}
            disabled={loading}
          />
          <SearchButton
            type="primary"
            size="large"
            onClick={handleSearch}
            loading={loading}
            disabled={!sku.trim()}
            icon={<SearchOutlined />}
          >
            Cerca
          </SearchButton>
        </SearchSection>
      </Card>

      {hasSearched && (
        <ResultsArea>
          {product ? (
            <>
              <Card>
                <Title level={4} style={{ marginBottom: 16 }}>
                  Dettagli prodotto
                </Title>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="SKU">
                    {product.custitem_velprod_item_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nome Prodotto">
                    {product.displayname}
                  </Descriptions.Item>
                  <Descriptions.Item label="Colore">
                    {product.custitem_velprod_colo_nameit}
                  </Descriptions.Item>
                  <Descriptions.Item label="Modello">
                    {product.custitem_velprod_model_nameit}
                  </Descriptions.Item>
                  <Descriptions.Item label="Suola">
                    {
                      product[
                        'CUSTITEM_VELPROD_SOLE.custrecord_velprod_sole_nameit'
                      ]
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label="Taglia">
                    {product.custitem1?.value}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
              <Card>
                <Title level={4} style={{ marginBottom: 16 }}>
                  Media Gallery
                </Title>
                {images.length > 0 ? (
                  <ImageGrid>
                    {images.map((src, index) => {
                      if (failedImages.has(index)) return null
                      return (
                        <ImageCell key={index}>
                          <ImageZoom
                            image={{
                              src: src,
                              alt: `Product ${index + 1}`,
                              onError: () =>
                                setFailedImages(
                                  prev => new Set([...prev, index]),
                                ),
                            }}
                            zoomImage={{
                              src: src,
                              alt: `Product ${index + 1}`,
                            }}
                          />
                        </ImageCell>
                      )
                    })}
                  </ImageGrid>
                ) : (
                  <p>Nessuna immagine disponibile</p>
                )}
              </Card>
            </>
          ) : (
            <Card style={{ flex: 1 }}>
              <Empty
                description={`Nessun prodotto trovato per SKU '${searchedSku}'`}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )}
        </ResultsArea>
      )}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  min-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`

const SearchInput = styled(Input)`
  flex: 1;
`

const SearchButton = styled(Button)`
  min-width: 120px;
`

const ResultsArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
`

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: min-content;
  gap: 12px;
  align-content: start;
`

const ImageCell = styled.div`
  overflow: hidden;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  max-height: 280px;

  img {
    max-width: 100%;
    max-height: 280px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    cursor: zoom-in;
  }
`

export default SearchProductInfo
